import { useContext } from "react";

import { CodeBlock } from "../../components/CodeBlock/CodeBlock";
import {
  API_KEY_VAR_PLACEHOLDER,
  ENDPOINT_SNIPPETS,
  FILE_PATH_VAR_PLACEHOLDER,
} from "../../constants/ApiCodeSnippets";
import { DEFAULT_DP_INFERENCE_CONFIG } from "../../constants/DpInferenceConfig";
import { ENDPOINT_CONFIGS, EndpointType } from "../../constants/Endpoints";
import { ValidatorFileContext } from "../../hooks/useFile";
import { InferenceConfig } from "../../types/demo";
import { getModelName, getResultFile } from "../../utils/file";

const ApiCodeContent = ({ endpointType }: { endpointType: EndpointType }) => {
  const { files, selectedFileIndex } = useContext(ValidatorFileContext);

  const config = ENDPOINT_CONFIGS[endpointType];

  // 설정된 정보가 없으면 아무것도 렌더링하지 않음 (오류 처리)
  if (!config || config.availableLanguages.length === 0) {
    console.error(
      `Configuration or code snippets not found for endpoint: ${endpointType}`,
    );
    return null;
  }

  // 사용 가능한 언어에 대한 pre 엘리먼트 배열 생성
  const preElements = config.availableLanguages.flatMap(langKey => {
    const snippetGenerator = ENDPOINT_SNIPPETS[endpointType]?.[langKey];

    // 스니펫 생성 함수가 없으면 빈 배열 반환
    if (typeof snippetGenerator !== "function") {
      console.warn(
        `Missing config or snippet generator for language "${langKey}" in endpoint "${endpointType}"`,
      );
      return [];
    }

    const file = getResultFile(files, selectedFileIndex);

    // 코드 스니펫 생성 (config 객체와 변수명 전달)
    const code = snippetGenerator(
      config,
      API_KEY_VAR_PLACEHOLDER,
      FILE_PATH_VAR_PLACEHOLDER,
      file?.inferenceConfig
        ? file.inferenceConfig
        : endpointType === "layout-analysis"
          ? DEFAULT_DP_INFERENCE_CONFIG
          : ({} as InferenceConfig),
      getModelName(files, selectedFileIndex),
    );

    // CodeBlock이 요구하는 형식으로 JSX 배열 반환
    return [
      <pre key={langKey}>
        <code className={`language-${langKey}`}>{code}</code>
      </pre>,
    ];
  }) as []; // flatMap 이후에도 타입 단언이 필요할 수 있음 (혹은 ChildrenPre[] 타입 보장 확인)

  // 생성된 pre 엘리먼트가 없으면 아무것도 렌더링하지 않음
  if (!preElements || preElements.length === 0) {
    return <p>No code examples available for this endpoint.</p>;
  }

  return <CodeBlock>{preElements}</CodeBlock>;
};

export default ApiCodeContent;
